import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, TrendingUp, Eye, Flame } from "lucide-react";
import { usePopularArticle, useLatestArticle } from "@/hooks/useArticles";
import { formatDistanceToNow } from "date-fns";

export const PopularNowCard = () => {
  const { data: popularArticle, isLoading: loadingPopular } = usePopularArticle();
  const { data: latestArticle } = useLatestArticle();

  // Fall back to latest article if no popular article has views yet
  const article = popularArticle?.view_count && popularArticle.view_count > 0 
    ? popularArticle 
    : null;

  // Don't show if same as latest article (to avoid duplication)
  if (article && latestArticle && article.id === latestArticle.id) {
    return null;
  }

  if (loadingPopular) {
    return (
      <section className="container mx-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <Skeleton className="h-8 w-40" />
        </div>
        <Skeleton className="h-[160px] rounded-xl" />
      </section>
    );
  }

  // Only show if we have a popular article with actual views
  if (!article) {
    return null;
  }

  const timeAgo = article.publish_date 
    ? formatDistanceToNow(new Date(article.publish_date), { addSuffix: true })
    : 'Recently';

  return (
    <section className="container mx-auto px-4 py-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-2 h-8 bg-orange-500 rounded-full" />
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          <h2 className="text-xl font-bold text-foreground">Popular Right Now</h2>
        </div>
      </div>

      <Link to={`/articles/${article.slug}`} className="group block">
        <Card className="overflow-hidden border-border/50 bg-gradient-to-r from-card via-card to-orange-500/5 hover:border-orange-500/50 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="gap-1 border-orange-500/30 text-orange-500">
                    <Eye className="w-3 h-3" />
                    {article.view_count?.toLocaleString() || 0} views
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {article.category?.replace('-', ' ')}
                  </Badge>
                </div>
                
                <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                  {article.headline}
                </h3>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeAgo}
                  </span>
                  <span>{article.reading_time || 5} min read</span>
                  {article.opportunity_score && (
                    <span className="flex items-center gap-1 text-green-500">
                      <TrendingUp className="w-3 h-3" />
                      Score: {article.opportunity_score}
                    </span>
                  )}
                </div>
              </div>
              
              {article.tokens_featured && article.tokens_featured.length > 0 && (
                <div className="flex gap-2 flex-wrap md:flex-nowrap">
                  {article.tokens_featured.slice(0, 2).map((token) => (
                    <Badge key={token} variant="secondary" className="text-xs">
                      {token}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    </section>
  );
};
